
import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { generateImageFromElement, downloadImage, copyImageToClipboard } from "@/utils/imageGenerationUtils";
import { shareToFacebook } from "@/utils/socialShareUtils";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Download, Facebook, Copy, Type, Image as ImageIcon, Palette, LayoutTemplate } from 'lucide-react';
import { Switch } from "@/components/ui/switch";

const standardTemplates = [
  { id: 1, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//1.png" },
  { id: 2, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//2.png" },
  { id: 3, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//3.png" },
  { id: 4, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//4.png" },
  { id: 5, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//5.png" },
  { id: 6, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//6.png" },
  { id: 7, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//7.png" },
  { id: 8, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//8.png" },
  { id: 9, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//9.png" },
  { id: 10, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//10.png" },
  { id: 11, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//11.png" },
  { id: 12, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//12.png" },
  { id: 13, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//13.png" },
  { id: 14, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//14.png" },
  { id: 15, url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//15.png" },
];

const mobileTemplates = [
  { id: 'mobile1', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile1.png" },
  { id: 'mobile2', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile2.png" },
  { id: 'mobile3', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile3.png" },
  { id: 'mobile4', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile4.png" },
  { id: 'mobile5', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile5.png" },
  { id: 'mobile6', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile6.png" },
  { id: 'mobile7', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile7.png" },
  { id: 'mobile8', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile8.png" },
  { id: 'mobile9', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile9.png" },
  { id: 'mobile10', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile10.png" },
  { id: 'mobile11', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile11.png" },
  { id: 'mobile12', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile12.png" },
  { id: 'mobile13', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile13.png" },
  { id: 'mobile14', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile14.png" },
  { id: 'mobile15', url: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Mobile15.png" },
];

const aspectRatios = [
  { id: 'standard', name: 'Standard (1:1)', ratio: '1/1' },
  { id: 'story', name: 'Story/Reels (9:16)', ratio: '9/16' },
];

const fontOptions = [
  { id: 'playfair', name: 'Playfair Display', className: 'font-playfair' },
  { id: 'montserrat', name: 'Montserrat', className: 'font-montserrat' },
];

const colorOptions = [
  { id: 'text-white', name: 'White', className: 'text-white' },
  { id: 'text-black', name: 'Black', className: 'text-black' },
  { id: 'text-blue-500', name: 'Blue', className: 'text-blue-500' },
  { id: 'text-red-500', name: 'Red', className: 'text-red-500' },
  { id: 'text-green-500', name: 'Green', className: 'text-green-500' },
  { id: 'text-yellow-500', name: 'Yellow', className: 'text-yellow-500' },
  { id: 'text-purple-500', name: 'Purple', className: 'text-purple-500' },
  { id: 'text-pink-500', name: 'Pink', className: 'text-pink-500' },
  { id: 'text-indigo-500', name: 'Indigo', className: 'text-indigo-500' },
];

interface VerseImageCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  verseText: string;
  verseReference: string;
}

const VerseImageCreator: React.FC<VerseImageCreatorProps> = ({ 
  isOpen, 
  onClose, 
  verseText, 
  verseReference 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | number>(() => {
    const saved = localStorage.getItem('selah-last-template');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [aspectRatio, setAspectRatio] = useState('standard');
  const [textColor, setTextColor] = useState('text-white');
  const [font, setFont] = useState('playfair');
  const [fontSize, setFontSize] = useState(100); // 100% is default
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasShadow, setHasShadow] = useState(true);
  const [shadowOpacity, setShadowOpacity] = useState(50);
  // Add background URL reference to prevent unnecessary reloads
  const [backgroundUrl, setBackgroundUrl] = useState<string>("");
  
  const imageRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  // Get the currently selected font class
  const selectedFont = fontOptions.find(f => f.id === font)?.className || 'font-playfair';
  
  // Truncate verse text if it's too long
  const truncatedVerseText = verseText.length > 200 
    ? verseText.substring(0, 197) + '...' 
    : verseText;

  // Get current templates based on aspect ratio
  const currentTemplates = aspectRatio === 'standard' ? standardTemplates : mobileTemplates;

  // Update background URL when template changes, but not during other adjustments
  useEffect(() => {
    const template = currentTemplates.find(t => t.id === selectedTemplate);
    if (template) {
      setBackgroundUrl(template.url);
    }
  }, [selectedTemplate, aspectRatio]);

  // Save template selection to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selah-last-template', selectedTemplate.toString());
  }, [selectedTemplate]);

  const handleDownload = async () => {
    if (!imageRef.current) return;
    
    setIsGenerating(true);
    try {
      const blob = await generateImageFromElement(imageRef.current);
      const filename = `${verseReference.replace(/\s+/g, '-').replace(/:/g, '-')}.png`;
      downloadImage(blob, filename);
      
      toast({
        title: "Success",
        description: "Image downloaded successfully!",
      });
    } catch (error) {
      console.error('Error downloading image:', error);
      toast({
        title: "Error",
        description: "Failed to download image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!imageRef.current) return;
    
    setIsGenerating(true);
    try {
      const blob = await generateImageFromElement(imageRef.current);
      const success = await copyImageToClipboard(blob);
      
      if (success) {
        toast({
          title: "Success",
          description: "Image copied to clipboard!",
        });
      } else {
        throw new Error('Failed to copy to clipboard');
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: "Error",
        description: "Failed to copy image. Your browser may not support this feature.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShareToFacebook = async () => {
    if (!imageRef.current) return;
    
    setIsGenerating(true);
    try {
      // For Facebook sharing
      shareToFacebook();
      
      toast({
        title: "Sharing",
        description: "Opening Facebook share dialog...",
      });
    } catch (error) {
      console.error('Error sharing to Facebook:', error);
      toast({
        title: "Error",
        description: "Failed to share to Facebook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getTextSize = () => {
    // Convert the fontSize percentage to a direct style value instead of using Tailwind classes
    // This gives us more precise control over the font size
    const baseSize = fontSize / 100; // Convert percentage to multiplier
    const length = truncatedVerseText.length;
    
    // Calculate base size depending on text length
    let baseFontSize;
    if (length > 150) {
      baseFontSize = { mobile: 14, desktop: 18 };
    } else if (length > 100) {
      baseFontSize = { mobile: 16, desktop: 20 };
    } else if (length > 50) {
      baseFontSize = { mobile: 18, desktop: 22 };
    } else {
      baseFontSize = { mobile: 20, desktop: 24 };
    }
    
    // Apply the fontSize percentage to get the final sizes
    return {
      fontSize: `${Math.max(10, Math.round(baseFontSize.mobile * baseSize))}px`,
      '@media (min-width: 768px)': {
        fontSize: `${Math.max(12, Math.round(baseFontSize.desktop * baseSize))}px`
      }
    };
  };

  const getReferenceFontSize = () => {
    const baseSize = fontSize / 100;
    return {
      fontSize: `${Math.max(10, Math.round(14 * baseSize))}px`
    };
  };

  const getShadowStyle = () => {
    if (!hasShadow) return {};
    const opacity = shadowOpacity / 100;
    return {
      textShadow: `0px 2px 4px rgba(0,0,0,${opacity})`
    };
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl h-[90vh] p-6 overflow-y-auto bg-white rounded-xl">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-2xl font-playfair text-heaven-gold">
            Create Verse Image
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Image Preview Side - Made responsive with fixed aspect ratio container */}
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md mx-auto rounded-lg bg-gray-100 border shadow-md">
              <div
                ref={imageRef}
                className="relative flex flex-col justify-center items-center p-6"
                style={{ 
                  backgroundImage: `url(${backgroundUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  width: '100%',
                  aspectRatio: aspectRatios.find(a => a.id === aspectRatio)?.ratio || '1/1'
                }}
              >
                <div className={`z-10 text-center flex flex-col items-center justify-center h-full w-full px-4`}>
                  <p 
                    className={`${selectedFont} ${textColor} font-semibold leading-tight mb-2 text-center`}
                    style={{ 
                      ...getTextSize(),
                      ...getShadowStyle(),
                      maxWidth: '100%',
                      overflowWrap: 'break-word',
                    }}
                  >
                    "{truncatedVerseText}"
                  </p>
                  <p 
                    className={`${selectedFont} ${textColor} font-medium text-center`}
                    style={{
                      ...getReferenceFontSize(),
                      ...getShadowStyle()
                    }}
                  >
                    {verseReference}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Controls Side */}
          <div className="flex flex-col space-y-4 overflow-y-auto">
            <Tabs defaultValue="ratio" className="w-full">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8">
                <TabsTrigger 
                  value="ratio" 
                  className="py-2 px-6 rounded-lg text-xs md:text-sm data-[state=active]:bg-[#34495E] data-[state=active]:text-white transition-colors flex items-center justify-center"
                >
                  <div className="flex items-center gap-2">
                    <LayoutTemplate className="h-4 w-4 mr-1" />
                    <span className="whitespace-nowrap">Ratio</span>
                  </div>
                </TabsTrigger>
                
                <TabsTrigger 
                  value="template" 
                  className="py-2 px-6 rounded-lg text-xs md:text-sm data-[state=active]:bg-[#34495E] data-[state=active]:text-white transition-colors flex items-center justify-center"
                >
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 mr-1" />
                    <span className="whitespace-nowrap">Background</span>
                  </div>
                </TabsTrigger>
                
                <TabsTrigger 
                  value="text" 
                  className="py-2 px-6 rounded-lg text-xs md:text-sm data-[state=active]:bg-[#34495E] data-[state=active]:text-white transition-colors flex items-center justify-center"
                >
                  <div className="flex items-center gap-2">
                    <Type className="h-4 w-4 mr-1" />
                    <span className="whitespace-nowrap">Font</span>
                  </div>
                </TabsTrigger>
                
                <TabsTrigger 
                  value="color" 
                  className="py-2 px-6 rounded-lg text-xs md:text-sm data-[state=active]:bg-[#34495E] data-[state=active]:text-white transition-colors flex items-center justify-center"
                >
                  <div className="flex items-center gap-2">
                    <Palette className="h-4 w-4 mr-1" />
                    <span className="whitespace-nowrap">Color</span>
                  </div>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="ratio" className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h3 className="font-medium text-lg mb-2">Select Aspect Ratio</h3>
                <div className="grid grid-cols-2 gap-4">
                  {aspectRatios.map((ratio) => (
                    <button
                      key={ratio.id}
                      onClick={() => {
                        setAspectRatio(ratio.id);
                        // Reset template selection when changing ratio
                        setSelectedTemplate(ratio.id === 'standard' ? 1 : 'mobile1');
                      }}
                      className={`py-3 px-4 rounded-md text-sm border-2 transition-all ${
                        aspectRatio === ratio.id 
                          ? 'border-heaven-gold bg-heaven-gold/10 font-medium' 
                          : 'border-gray-200 hover:border-heaven-gold/50'
                      }`}
                    >
                      {ratio.name}
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="template" className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h3 className="font-medium text-lg mb-2">Select Background Template</h3>
                <div className="grid grid-cols-5 gap-2">
                  {currentTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`aspect-square rounded-md overflow-hidden border-2 transition-all hover:scale-105 ${
                        selectedTemplate === template.id 
                          ? 'border-heaven-gold shadow-lg' 
                          : 'border-transparent hover:border-heaven-gold/50'
                      }`}
                    >
                      <img 
                        src={template.url} 
                        alt={`Template ${template.id}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="text" className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h3 className="font-medium text-lg mb-2">Font Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-1">Font Family</label>
                    <Select value={font} onValueChange={(value) => setFont(value)}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select font" />
                      </SelectTrigger>
                      <SelectContent>
                        {fontOptions.map(option => (
                          <SelectItem key={option.id} value={option.id} className={option.className}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium block mb-1">Font Size</label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[fontSize]}
                        onValueChange={(value) => setFontSize(value[0])}
                        min={50}
                        max={150}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-sm w-12 text-center font-medium bg-gray-100 rounded py-1">
                        {fontSize}%
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <label className="text-sm font-medium block mb-2">Text Shadow</label>
                    <div className="flex items-center space-x-3 mb-3">
                      <Switch
                        checked={hasShadow}
                        onCheckedChange={setHasShadow}
                        className="data-[state=checked]:bg-heaven-gold"
                      />
                      <span className="text-sm">Enable shadow</span>
                    </div>
                    
                    {hasShadow && (
                      <div className="mt-3">
                        <label className="text-sm block mb-2">Shadow Intensity</label>
                        <div className="flex items-center gap-4">
                          <Slider
                            value={[shadowOpacity]}
                            onValueChange={(value) => setShadowOpacity(value[0])}
                            min={20}
                            max={100}
                            step={10}
                            className="flex-1"
                          />
                          <span className="text-sm w-12 text-center font-medium bg-gray-100 rounded py-1">
                            {shadowOpacity}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="color" className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <h3 className="font-medium text-lg mb-2">Text Color</h3>
                <div className="grid grid-cols-3 gap-3">
                  {colorOptions.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => setTextColor(color.id)}
                      className={`py-3 px-3 rounded-md text-sm transition-all hover:scale-105 ${
                        textColor === color.id 
                          ? 'ring-2 ring-heaven-gold ring-offset-2' 
                          : 'hover:ring-1 hover:ring-heaven-gold/50'
                      } ${color.className} ${
                        color.id === 'text-white' 
                          ? 'bg-gray-800' 
                          : color.id === 'text-black' 
                            ? 'bg-white border' 
                            : 'bg-white'
                      }`}
                    >
                      {color.name}
                    </button>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="flex flex-wrap gap-3 py-3 mt-auto">
              <Button
                variant="gold"
                size="lg"
                className="flex-1 font-medium"
                onClick={handleDownload}
                disabled={isGenerating}
              >
                <Download className="mr-2 h-5 w-5" />
                Download Image
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="flex-1 border-heaven-gold text-heaven-gold hover:bg-heaven-gold/10"
                onClick={handleCopyToClipboard}
                disabled={isGenerating}
              >
                <Copy className="mr-2 h-5 w-5" />
                Copy
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="flex-1 border-heaven-gold text-heaven-gold hover:bg-heaven-gold/10"
                onClick={handleShareToFacebook}
                disabled={isGenerating}
              >
                <Facebook className="mr-2 h-5 w-5" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerseImageCreator;
